import ClosedCaptionOutlinedIcon from "@mui/icons-material/ClosedCaptionOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, Chip, Link, Stack, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridRowParams, GridRowsProp } from "@mui/x-data-grid";
import moment from "moment";
import React, { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import FileAccessibilityChip from "../../../../../Components/Misc/Chips/FileAccessibilityChip/file-accessibility-chip";
import FileStatusChip from "../../../../../Components/Misc/Chips/FileStatusChip/file-status-chip";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../../Components/Misc/ListPageHeader";
import { ModalContext } from "../../../../../Components/Modals";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import ModalFileUserDataHistory, {
  ModalFileUserDataHistoryElementProps,
} from "../../../../../Components/Modals/ModalFileUserDataHistory/modal-file-user-data-history";
import ModalRequestDownloadLink, {
  ModalRequestDownloadLinkElementProps,
} from "../../../../../Components/Modals/ModalRequestDownloadLink/modal-request-download-link";
import ModalFileDetails, {
  ModalFileDetailsElementProps,
} from "../../../../../Components/Modals/ModalShowDetails/modal-file-details";
import ModalSubtitleChecker, {
  ModalSubtitleCheckerElementProps,
} from "../../../../../Components/Modals/ModalSubtitleChecker/modal-subtitle-checker";
import ModalUploadMediaFiles, {
  ModalUploadMediaFilesElementProps,
} from "../../../../../Components/Modals/ModalUploadMediaFiles/modal-upload-media-files";
import DataTable, { CellSize } from "../../../../../Components/Table/table";
import TableCellTypography, {
  TableCellLongTypography,
} from "../../../../../Components/Table/TableCellTypography/table-cell-typography";
import TableSkeleton from "../../../../../Components/Table/TableSkeleton";
import { cacheService, mediaService } from "../../../../../Core/Api/services";
import { AclTypes } from "../../../../../Core/Api/Types/acl-types";
import { DbFileInfos } from "../../../../../Core/Api/Types/db-file-infos";
import { DbFileUserDataHistoryInfos } from "../../../../../Core/Api/Types/db-file-user-data-history-infos";
import { FileAccessibility } from "../../../../../Core/Api/Types/file-accessibility";
import { FileCategory } from "../../../../../Core/Api/Types/file-category";
import { FileStatus } from "../../../../../Core/Api/Types/file-status";
import { ObjectClass } from "../../../../../Core/Api/Types/object-class";
import { ObjectType } from "../../../../../Core/Api/Types/object-type";
import { PermissionType } from "../../../../../Core/Api/Types/permission-type";
import { PlatformMode } from "../../../../../Core/Api/Types/platform-mode";
import { IsDriveObject, IsPermissionGranted } from "../../../../../Core/Utils/asset-object-utils";
import GlobalConst from "../../../../../Core/Utils/Constants/globals";
import {
  DeleteMediaFile,
  GetObject,
  GetObjectMediaFiles,
  UpdateMediaFile,
} from "../../../../../Redux/Reducers/redux-slice-asset";
import { useAppDispatch, useAppSelector } from "../../../../../Redux/redux-store";
import { GetFileSize, UserHasAcl } from "../../../../../Utils/helpers";
import useCustomSnackbar from "../../../../../Utils/Hooks/useCustomSnackbar";
import useDidMountEffect from "../../../../../Utils/Hooks/useComponentDidMountEffect";
import { ObjectStatus } from "../../../../../Core/Api/Types/object-status";
import ModalChooseUploadMethod, {
  ModalChooseUploadMethodProps,
} from "../../../../../Components/Modals/ModalChooseUploadMethod/modal-choose-upload-method";
import TableUuids from "../../../../../Components/Table/table-uuids";

const AssetSingleMediaFilesContent = (props: { refreshMediaFiles: () => void }) => {
  const { refreshMediaFiles } = props;

  const { t } = useTranslation();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const mediaFiles = useAppSelector(GetObjectMediaFiles);
  const [selectedMediaFiles, setSelectedMediaFiles] = React.useState<number[]>([]);
  const [uploadOk, setUploadOk] = React.useState<number>(0);

  const dispatch = useAppDispatch();
  const customSnackbar = useCustomSnackbar();

  const authKey = cacheService.GetAuthKey();
  const isSuperUser = UserHasAcl(AclTypes.aclSuperUser);
  const obj = useSelector(GetObject);

  const uploadInputRef = useRef<any>(undefined);

  const handleClickSubtitleChecker = (fileId: number) => {
    if (!openModal || !obj) return;

    openModal<ModalSubtitleCheckerElementProps>(ModalSubtitleChecker, {
      id: fileId,
      assetId: obj.id,
    });
  };

  const handleClickDownloadLink = () => {
    if (!openModal) return;

    openModal<ModalRequestDownloadLinkElementProps>(ModalRequestDownloadLink, {
      fileIds: selectedMediaFiles,
    });
  };

  const handleClickUploadMedia = useCallback(() => {
    if (!openModal || !obj) return;

    let driveReuploadFile =
      IsDriveObject(obj) && (obj.status === ObjectStatus.Rejected || obj.status === ObjectStatus.RejectedAuto);

    if (!driveReuploadFile)
      openModal<ModalUploadMediaFilesElementProps>(ModalUploadMediaFiles, {
        objectId: obj.id,
        objectType: obj?.type ?? ObjectType.Unknown,
        onSuccess: () => {
          setUploadOk((prev) => prev + 1);
        },
      });
    else
      openModal<ModalChooseUploadMethodProps>(ModalChooseUploadMethod, {
        assetId: obj.id,
        assetName: obj.name,
        assetParentName: obj.parentObject?.name || obj.parentTitleName || "",
        assetType: obj.type,
        assetClass: obj.classification ?? ObjectClass.Drive,
        closeModal: () => {},
        inputRef: uploadInputRef.current,
      });
  }, [obj]);

  const handleShowInfosMF = (file: DbFileInfos) => {
    if (!openModal) return;

    openModal<ModalFileDetailsElementProps>(ModalFileDetails, {
      file: file,
    });
  };

  const handleSynchronizeMF = (fileId: number) => {
    mediaService.SyncMediaFile(fileId).then((response) => {
      if (response.file && response.success) {
        dispatch(UpdateMediaFile(response.file));
        customSnackbar(t("alert:success.fileHasBeenSynced"), "success");
      } else {
        customSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
      }
    });
  };

  const handleDeleteMF = (fileId: number) => {
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: false,
      title: t("modal:title.confirmFileDeletion"),
      onConfirm: () => {
        mediaService.DeleteMediaFile(fileId).then((response) => {
          if (response.success) {
            dispatch(DeleteMediaFile(fileId));
            customSnackbar(t("alert:success.fileHasBeenDeleted"), "success");
          } else customSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
        });
      },
    });
  };

  const openModalUserDataHistory = (event, file: DbFileInfos, userDataHistory: DbFileUserDataHistoryInfos | null) => {
    event.stopPropagation();
    if (!openModal) return;

    openModal<ModalFileUserDataHistoryElementProps>(ModalFileUserDataHistory, {
      file: file,
      data: userDataHistory,
    });
  };

  const isDownloadable = (row: DbFileInfos) => {
    return (
      ((cacheService.GetPlatformMode() === PlatformMode.Browser ||
        cacheService.GetPlatformMode() === PlatformMode.Adam ||
        obj?.classification === ObjectClass.Drive) &&
        (row.accessibility === FileAccessibility.Disk || row.accessibility === FileAccessibility.DiskAndTape)) ||
      (row.formatInfos !== null &&
        row.formatInfos?.isDownloadable &&
        (!row.formatInfos.isRestricted || UserHasAcl(AclTypes.aclRestrictedFileDownload)) &&
        IsPermissionGranted(obj, PermissionType.Download))
    );
  };

  // table creation
  const _rows: GridRowsProp = [];
  type Row = (typeof _rows)[number];

  const columnsDefinition: GridColDef[] = [
    {
      field: "name",
      headerName: t("table:headerName.fileName"),
      minWidth: CellSize.xlarge,
      flex: CellSize.xlarge,
      renderCell: (data: Row) =>
        isDownloadable(data.row) ? (
          <Link
            href={`${mediaService.GetServiceUrl()}/media-infos/object/${data?.row?.objectId}/${
              data?.row?.id
            }/${authKey}`}
            underline="hover"
            target="_blank"
            fontWeight={500}
            p={1}
            variant="body1"
            sx={{ wordBreak: "break-word" }}
          >
            {data.value}
          </Link>
        ) : (
          <TableCellLongTypography data={data.value} />
          //   <Typography p={1} variant="body2" sx={{ wordBreak: "break-word" }}>
          //     {data.value}
          //   </Typography>
        ),
    },
    {
      field: "formatInfos",
      headerName: t("table:headerName.fileType"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      type: "singleSelect",
      valueOptions: (params: any) => {
        const result: string[] = [];
        if (!mediaFiles) return result;
        for (let i = 0; i < mediaFiles?.length; i++) {
          const formatName = mediaFiles[i].formatInfos?.name || "Not defined";
          if (!result.find((x) => x === formatName)) result.push(formatName);
        }
        return result;
      },
      valueGetter: (data: any, row: any) => row?.formatInfos?.name || "Not defined",
      renderCell: (data: Row) => (
        <Box sx={{ flexWrap: "wrap" }}>
          <Chip label={data.value} component="span" variant="outlined" size="small" />
        </Box>
      ),
    },
    {
      field: "date",
      headerName: t("common:date"),
      type: "date",
      minWidth: CellSize.small,
      flex: CellSize.small,
      valueGetter: (data: Row, row: any) => new Date(row?.creationInfos?.date),
      renderCell: (data: Row) => (
        <TableCellTypography
          variant="body1"
          color="text.secondary"
          data={moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
        />
      ),
    },
    {
      field: "sizeInBytes",
      headerName: t("common:size"),
      minWidth: CellSize.small,
      flex: CellSize.small,
      renderCell: (data: Row) => (
        <TableCellTypography variant="body1" color="text.secondary" data={GetFileSize(data.value)} />
      ),
    },
    {
      field: "duration",
      headerName: t("common:duration"),
      minWidth: CellSize.small,
      flex: CellSize.small,
      valueGetter: (data: any, row: any) => row?.properties?.duration?.timecode,
      renderCell: (data: Row) => (
        <Typography variant="body1" component="span" color="text.secondary">
          {data.value}
        </Typography>
      ),
    },
    {
      field: "category",
      type: "singleSelect",
      valueOptions: Object.keys(FileCategory),
      headerName: t("common:category"),
      minWidth: CellSize.xsmall,
      flex: CellSize.xsmall,
    },
    {
      field: "status",
      headerName: t("common:status"),
      type: "singleSelect",
      valueOptions: Object.keys(FileStatus),
      minWidth: CellSize.small,
      flex: CellSize.small,
      renderCell: (data: Row) => <FileStatusChip variant="outlined" status={data.value} />,
    },
    {
      field: "storageInfos",
      headerName: t("common:storage"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      renderCell: (data: Row) => data.value.name,
    },
    {
      field: "accessibility",
      headerName: t("common:accessibility"),
      type: "singleSelect",
      valueOptions: Object.keys(FileAccessibility),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      renderCell: (data: Row) => <FileAccessibilityChip variant="outlined" accessibility={data.value} />,
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      align: "right",
      minWidth: CellSize.actionsSmall,
      flex: CellSize.actionsSmall,
      getActions: (params: GridRowParams) => {
        if (!params || !params.row) return [];
        let array: any[] = [
          <GridActionsCellItem
            icon={<VisibilityIcon sx={{ width: 20, height: 20 }} />}
            label={t("table:cellTitle.showInfos")}
            title={t("table:cellTitle.showInfos")}
            showInMenu
            onClick={() => handleShowInfosMF(params.row)}
          />,
          <GridActionsCellItem
            icon={<SyncOutlinedIcon sx={{ width: 20, height: 20 }} color="primary" />}
            label={t("common:sync")}
            title={t("common:synchronize")}
            color="info"
            showInMenu
            disabled={!UserHasAcl(AclTypes.aclSuperUser)}
            onClick={() => handleSynchronizeMF(params.row["id"])}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon sx={{ width: 20, height: 20 }} color="error" />}
            label={t("common:delete")}
            title={t("common:delete")}
            color="error"
            showInMenu
            disabled={
              params?.row?.formatInfos === null ||
              !params?.row?.formatInfos.isDeletable ||
              !params?.row?.storageInfos.allowDelete
            }
            onClick={() => handleDeleteMF(params.row["id"])}
          />,
        ];

        if (
          params.row["formatInfos"] &&
          params.row["formatInfos"].objectType === ObjectType.Subtitle &&
          params.row["extension"] === ".ttml"
        )
          array.push(
            <GridActionsCellItem
              icon={<ClosedCaptionOutlinedIcon sx={{ width: 20, height: 20 }} />}
              label={t("table:cellTitle.subtitleChecker")}
              title={t("table:cellTitle.subtitleChecker")}
              showInMenu
              disabled={!UserHasAcl(AclTypes.aclFileSubtitleCheck)}
              onClick={() => handleClickSubtitleChecker(params.row["id"])}
            />
          );

        return array;
      },
    },
  ];

  if (UserHasAcl(AclTypes.aclFileUserDataAdd)) {
    const userDataColumn = {
      field: "userDataHistory",
      headerName: t("table:headerName.userData"),
      width: 200,
      renderCell: (data: Row) => {
        if (data.value === null)
          return (
            <Link
              onClick={(e) => openModalUserDataHistory(e, data.row, null)}
              variant="body1"
              sx={{ cursor: "pointer", color: "#2D6CE1" }}
            >
              {t("button:addUserData")}
            </Link>
          );
        else {
          const userData = data.value[data.value.length - 1];
          return (
            <Stack
              onClick={(e) => openModalUserDataHistory(e, data.row, userData)}
              direction="column"
              spacing={0.5}
              p={1}
            >
              {userData.type !== null && (
                <Chip label={userData.type} color="primary" size="small" sx={{ cursor: "pointer" }} />
              )}
              {userData.customType !== null && (
                <Chip label={userData.customType} size="small" sx={{ cursor: "pointer" }} />
              )}
              {userData.status !== null && <Chip label={userData.status} size="small" sx={{ cursor: "pointer" }} />}
            </Stack>
          );
        }
      },
    };

    columnsDefinition.splice(columnsDefinition.length - 1, 0, userDataColumn);
  }

  useDidMountEffect(() => {
    refreshMediaFiles();
  }, [uploadOk]);

  const additionalActions = (
    <>
      <Button
        disabled={
          selectedMediaFiles.length === 0 ||
          !UserHasAcl(AclTypes.aclMakeTransferFileDownloadLink) ||
          !IsPermissionGranted(obj, PermissionType.Download)
        }
        onClick={handleClickDownloadLink}
        startIcon={<FileDownloadOutlinedIcon />}
        variant="outlined"
      >
        {t("modal:title.createDownloadLink")}
      </Button>
      {cacheService.GetPlatformMode() !== PlatformMode.Browser && (
        <Button onClick={handleClickUploadMedia} variant="contained" startIcon={<FileUploadOutlinedIcon />}>
          {t("button:uploadNewMedia")}
        </Button>
      )}
    </>
  );

  return (
    <Stack sx={{ minHeight: "calc(100vh - 129px)" }}>
      {!mediaFiles ? (
        <TableSkeleton />
      ) : (
        <DataTable
          tableKey={TableUuids.ASSET_SINGLE_MEDIA_FILES}
          rows={mediaFiles}
          columns={columnsDefinition}
          selectedRows={selectedMediaFiles}
          additionalActions={additionalActions}
          setSelectedRows={setSelectedMediaFiles}
          getRowHeight={() => "auto"}
          checkboxSelection
          disableRowSelectionOnClick
          isRowSelectable={(params: GridRowParams) =>
            ((cacheService.GetPlatformMode() === PlatformMode.Browser ||
              cacheService.GetPlatformMode() === PlatformMode.Adam ||
              obj?.classification === ObjectClass.Drive) &&
              (params?.row?.accessibility === FileAccessibility.Disk ||
                params?.row?.accessibility === FileAccessibility.DiskAndTape)) ||
            (params?.row?.formatInfos !== null &&
              params?.row?.formatInfos?.isDownloadable &&
              (!params?.row?.formatInfos.isRestricted || UserHasAcl(AclTypes.aclRestrictedFileDownload)))
          }
          onRowClick={(e) => isSuperUser && handleShowInfosMF(e.row as DbFileInfos)}
          initialState={{ pinnedColumns: { left: ["__check__", "name"], right: ["actions"] } }}
        />
      )}
    </Stack>
  );
};

export default AssetSingleMediaFilesContent;
